import React, { useState, useEffect, useRef, useCallback } from 'react';
import { reverseGeocode } from '../../utils/geocoding';
import './SinglePostHelper.css';
import moment from 'moment';
import { Heart, Share2, Gauge, X, Trash2, Eye, Copy, Check, Loader2 } from 'lucide-react';
import SharingTab from '../AllPosts/SharingTab';
import { makeNumber } from '../../utils/calc';
import { useUser } from '../../UserContext';
import { BACKEND_URL, WEB_BASE_URL, mediaUrl, profilePicUrl } from '../../config';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DefaultPFP from '../../Media/Icons/DefaultPFP.webp';
import UnknownAuthority from '../../Media/Icons/UnknownAuthority.webp';
import Anonymous from '../../Media/Icons/anonymous.png';
import RiskoMeter from '../RiskoMeter/RiskoMeter';
import SophisticatedAddress from '../Common/SophisticatedAddress';
import { formatCustomDistanceToNow } from '../../utils/timeUtils';
import { isHttpUrl } from '../../utils/url';
import { useData } from '../../DataContext';

// Geocoding now uses Nominatim (OpenStreetMap) via centralized utility in src/utils/geocoding.ts

export type Comments = {
	commentId: number;
	parentId: number | null;
	isAnonymous: boolean;
	user: {
		name: string;
		id: number;
		profilepicURL: string;
	};
	likeCount: number;
	createdAt: string;
	body: string;
};

export type AllPostDataType = {
	id: number;
	title: string;
	content: string;
	format: string;
	image_url: any;
	name: string;
	location: string;
	time: string;
	commentCount: number;
	comments: Comments[];
	likeCount: number;
	country: string | null;
	state: string | null;
	city: string | null;
	tags: string;
	profilepicURL: string;
	isLiked: number | boolean;
	webLink: string | null;
	youtubeLink: string | null;
	internalURL: string | null;
	isAnonymous: boolean;
	isOwner: boolean;
	address_parts?: string[];
	shareHash?: string;
};

export type User = {
	id: number;
	phoneNumber: number;
	name: string;
	isBlocked: boolean;
	gender: string;
	dob: string;
	email: string;
	country: string;
	state: string;
	district: string;
	pincode: number;
	likeCount: number;
	mediaCount: number;
	postCount: number;
	followerCount: number;
	reportCount: number;
	is_authority: boolean;
	profilepicURL: string;
	// Institute entry number saved from the user's last report (null until one
	// is submitted). Prefills the QR report form.
	entryNumber?: string | null;
};

interface CustomModalProps {
	show: boolean;
	handleClose: () => void;
	imageUrl: string;
}

const CustomModal: React.FC<CustomModalProps> = ({ show, handleClose, imageUrl }) => {
	if (!show) return null;

	return (
		<div className='custom-modal-overlay'>
			<div className='custom-modal-content'>
				<button className='custom-modal-close' onClick={handleClose}>
					×
				</button>
				<img src={imageUrl} alt='Full View' className='custom-modal-image' />
			</div>
		</div>
	);
};

const LoadingSpinner = () => (
	<div className='flex justify-center flex-col items-center h-screen bg-gray-100'>
		<div className='w-12 h-12 border-4 border-gray-400 border-dashed rounded-full animate-spin'></div>
	</div>
);

const PostPlaceholderSVG = () => (
	<div className="w-full h-full bg-gray-100 overflow-hidden relative flex items-center justify-center animate-shimmer-wave">
		<svg className="w-32 h-32 text-gray-300 z-10 drop-shadow-sm relative" fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</svg>
	</div>
);

const PostImage = ({
	src,
	alt,
	className,
	onClick,
}: {
	src: string;
	alt: string;
	className?: string;
	onClick?: () => void;
}) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [hasError, setHasError] = useState(false);

	if (hasError)
		return (
			<div
				className={`${className || ''} aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100`}
				onClick={onClick}
			>
				<PostPlaceholderSVG />
			</div>
		);

	return (
		<div
			className={`relative aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100 ${className || ''}`}
			onClick={onClick}
		>
			{!isLoaded && (
				<div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
					<PostPlaceholderSVG />
				</div>
			)}
			<img
				src={src}
				alt={alt}
				loading='lazy'
				className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out cursor-pointer ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
				onLoad={() => setIsLoaded(true)}
				onError={() => setHasError(true)}
			/>
		</div>
	);
};

const ProfileImage = ({
	name,
	pfpURL,
	className,
	isAnonymous,
}: {
	name: string;
	pfpURL?: string | null;
	className?: string;
	isAnonymous?: boolean;
}) => {
	const isAuth = name.toLowerCase().includes('authority');
	const [hasError, setHasError] = useState(false);

	const src = isAnonymous
		? Anonymous
		: !hasError && pfpURL && pfpURL !== 'null' && pfpURL !== ''
			? profilePicUrl(pfpURL)
			: isAuth
				? UnknownAuthority
				: DefaultPFP;

	return <img src={src} className={className} alt={name} onError={() => setHasError(true)} />;
};

interface SinglePostHelperProps {
	// Read-only "share mode" — rendered for logged-out visitors who opened a
	// signed /share/<id>/<hash> link. All interactions are gated behind a login
	// prompt and the post is fetched via the public getSharedPost endpoint.
	shareMode?: boolean;
	shareId?: string;
	shareHash?: string;
}

function SinglePostHelper({ shareMode = false, shareId, shareHash }: SinglePostHelperProps = {}) {
	const { user, isLoggedIn, isAuthLoading, isAdmin } = useUser();
	const { updatePostMetadata } = useData();
	const { postId: paramPostId } = useParams<{ postId?: string }>();
	const location = useLocation();

	// The effective post id: from the route param in normal mode, from the prop
	// in share mode.
	const postId = shareMode ? shareId : paramPostId;

	// True when this page was reached by following a share link (the logged-in
	// redirect from SharedPost). Controls where "back" should land.
	const fromShare = (location.state as any)?.fromShare === true;

	// Where the back button (and post-delete bounce) should go:
	//  - share mode (logged out)      → Home
	//  - opened via share (logged in) → News
	//  - normal in-app navigation     → browser back (-1)
	const backTarget: string | null = shareMode ? '/' : fromShare ? '/news' : null;

	// Login prompt shown when a logged-out share-mode viewer tries to interact.
	const [showLoginPrompt, setShowLoginPrompt] = useState(false);

	const [AllPostData, set_AllPostData] = useState<AllPostDataType>();
	const [Oldest, set_Oldest] = useState<boolean>(false);
	const [CommentParentId, set_CommentParentId] = useState<number | null>(null);
	const [CommentName, set_CommentName] = useState<string>('');
	const [CommentBody, set_CommentBody] = useState<string>('');
	const [isLiked, setIsLiked] = useState(Boolean);
	const [showModal, setShowModal] = useState(false);
	const [isShareDropdownOpen, setShareDropdownOpen] = useState(false);
	const [isSortDropdownOpen, setSortDropdownOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [riskoMeterMediaId, setRiskoMeterMediaId] = useState<string | null>(null);
	const [openCommentDotsId, setOpenCommentDotsId] = useState<number | null>(null);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [toastMessage, setToastMessage] = useState<string | null>(null);
	const [postGone, setPostGone] = useState(false);

	// Admin-only "reveal author" for anonymous posts. Deliberately NOT cached:
	// the identity lives only in this state while the popup is open — closing it
	// wipes it, and revealing again re-asks the backend (each reveal is audited).
	const [revealedAuthor, setRevealedAuthor] = useState<{ name: string; phoneNumber: string } | null>(null);
	const [isRevealing, setIsRevealing] = useState(false);
	const [copiedField, setCopiedField] = useState<string | null>(null);
	// The admin panel (and this reveal control) is desktop-only — mirror the
	// lg (1024px) breakpoint used by AdminPanel.tsx.
	const [isDesktop, setIsDesktop] = useState<boolean>(() => window.innerWidth >= 1024);
	useEffect(() => {
		const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);
	const commentInputRef = useRef<HTMLInputElement>(null);
	const shareDropdownRef = useRef<HTMLDivElement>(null);
	const sortDropdownRef = useRef<HTMLDivElement>(null);
	const commentDropdownRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	const handleOpenModal = () => setShowModal(true);
	const handleCloseModal = () => setShowModal(false);

	// Auto-dismiss the toast after a few seconds (covers error toasts that don't
	// navigate away on their own).
	useEffect(() => {
		if (!toastMessage) return;
		const t = setTimeout(() => setToastMessage(null), 3000);
		return () => clearTimeout(t);
	}, [toastMessage]);

	const returnToFeed = () => {
		if (backTarget) navigate(backTarget);
		else navigate(-1);
	};

	// Logged-out visitors must never reach the normal (auth-required) post page —
	// getPost now 401s for them. Bounce them home. (Share mode handles logged-out
	// itself, so it's excluded.)
	useEffect(() => {
		if (shareMode) return;
		if (!isAuthLoading && !isLoggedIn) {
			navigate('/', { replace: true });
		}
	}, [shareMode, isAuthLoading, isLoggedIn, navigate]);

	// Make the browser/hardware back button land on the right in-app page when
	// this view was an entry point (share link), instead of leaving the site or
	// hitting a dead end. We push a sentinel entry and intercept the pop.
	useEffect(() => {
		if (!backTarget) return;
		window.history.pushState(null, '', window.location.href);
		const onPop = () => navigate(backTarget, { replace: true });
		window.addEventListener('popstate', onPop);
		return () => window.removeEventListener('popstate', onPop);
	}, [backTarget, navigate]);

	// Wipe the cached feed/scroll state for the feeds the user may have come
	// from, then go back. With no saved state the previous page refetches from
	// scratch and lands at the top — so a post that was just deleted (here or
	// from another device) disappears cleanly.
	const goBackToFreshFeed = useCallback(() => {
		['newsFeedState', 'newsScrollPos', 'exploreFeedState', 'exploreScrollPos'].forEach((key) =>
			sessionStorage.removeItem(key)
		);
		if (postId) {
			updatePostMetadata(parseInt(postId, 10), { commentCount: 0, likeCount: 0 });
		}
		if (backTarget) navigate(backTarget);
		else navigate(-1);
	}, [postId, updatePostMetadata, navigate, backTarget]);

	// Called whenever the backend tells us the post is gone — either up front
	// (opening a deleted post) or mid-session (liking/commenting on one deleted
	// elsewhere). Swap the view for a brief "no longer exists" notice, then
	// bounce back to a freshly-loaded feed. Guard against double-firing.
	const handlePostGone = useCallback(() => {
		setPostGone((already) => {
			if (!already) {
				setTimeout(() => {
					goBackToFreshFeed();
				}, 1200);
			}
			return true;
		});
	}, [goBackToFreshFeed]);

	// Fetch the author's identity for this (anonymous) post. Called on every
	// click of the Reveal button — nothing is remembered between reveals.
	const handleRevealAuthor = async () => {
		if (!postId || isRevealing) return;
		setIsRevealing(true);
		try {
			const res = await fetch(`${BACKEND_URL}/users/post/${postId}/author`, {
				method: 'GET',
				credentials: 'include' as RequestCredentials,
			});
			if (res.ok) {
				const data = await res.json();
				setRevealedAuthor({ name: data.name, phoneNumber: String(data.phoneNumber) });
			} else {
				setToastMessage('Could not reveal the author.');
			}
		} catch (err) {
			console.error('Error revealing author:', err);
			setToastMessage('Could not reveal the author.');
		} finally {
			setIsRevealing(false);
		}
	};

	const closeRevealPopup = () => {
		// Forget the identity entirely — next reveal must re-ask the backend.
		setRevealedAuthor(null);
		setCopiedField(null);
	};

	const handleRevealCopy = (field: string, text: string) => {
		navigator.clipboard.writeText(text);
		setCopiedField(field);
		setTimeout(() => setCopiedField(null), 2000);
	};

	const handleDeletePost = async () => {
		if (!postId || isDeleting) return;
		setIsDeleting(true);
		try {
			const res = await fetch(`${BACKEND_URL}/api/deletePost/${postId}`, {
				method: 'DELETE',
				redirect: 'follow' as RequestRedirect,
				credentials: 'include' as RequestCredentials,
				headers: {
					'Content-Type': 'application/json',
				},
			});
			// 404 → already deleted elsewhere; treat as success for the user.
			if (res.ok || res.status === 404) {
				setShowDeleteConfirm(false);
				setToastMessage('Post deleted.');
				setTimeout(() => {
					goBackToFreshFeed();
				}, 900);
			} else {
				setIsDeleting(false);
				setToastMessage('Failed to delete post. Please try again.');
			}
		} catch (err) {
			console.error('Error deleting post:', err);
			setIsDeleting(false);
			setToastMessage('Failed to delete post. Please try again.');
		}
	};

	useEffect(() => {
		if (!postId) {
			console.error('PostId is undefined or not found in route parameters.');
			return;
		}

		const getEmbeddedLink = (link: string): string[] => {
			let res = ['', ''];
			const regex =
				/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^\s]*)/;
			const match = link.match(regex);
			match ? (res[0] = `https://www.youtube.com/embed/${match[1]}`) : (res[1] = link);
			return res;
		};

		const fetchData = async () => {
			const requestOptions = {
				method: 'GET',
				redirect: 'follow' as RequestRedirect,
				credentials: 'include' as RequestCredentials,
				headers: {
				},
			};
			try {
				// Share mode hits the public signed endpoint; normal mode the
				// auth-required numeric endpoint.
				const fetchUrl = shareMode
					? `${BACKEND_URL}/api/getSharedPost/${shareId}/${shareHash}`
					: `${BACKEND_URL}/api/getPost/${postId}`;
				const response = await fetch(fetchUrl, requestOptions);
				// Post was deleted (404, or a 200 with an empty/null body from getPost).
				// Don't render the static "not found" screen — bounce back to a fresh feed.
				if (response.status === 404) {
					handlePostGone();
					return;
				}
				if (!response.ok) throw new Error('Post not found');
				const result = await response.json();
				if (!result) {
					handlePostGone();
					return;
				}
				let webLink = '';
				let youtubeLink = '';
				let file = '';
				let mediaFormat = 'unknown';
				let isImage = false;

				if (result.media && result.media[0]) {
					mediaFormat = result.media[0].format || 'unknown';
					if (mediaFormat.substring(0, 5) === 'image') {
						isImage = true;
					}

					if (result.media[0].link) {
						let links = getEmbeddedLink(result.media[0].link);
						if (links[0] === '') {
							// The web link is attacker-controlled (post author). Only embed it in
							// the <iframe> below if it's a plain http(s) URL — never javascript:/
							// data:/intent: etc. This page is also the public /share/<id>/<hash>
							// view, so the guard must hold for logged-out visitors too.
							webLink = isHttpUrl(links[1]) ? links[1] : '';
						} else {
							youtubeLink = links[0];
						}
					}

					if (isImage && result.media[0].internalURL) {
						file = mediaUrl(result.media[0].internalURL);
					}
				}

				const postData = result.isAnonymous
					? {
							id: postId ? parseInt(postId, 10) : 0,
							title: result.title as string,
							content: result.body as string,
							format: mediaFormat.substring(0, 5),
							image_url: file,
							name: 'Anonymous',
							location:
								result.media && result.media[0] ? `${result.media[0].latitude},${result.media[0].longitude}` : '',
							time: `${result.createdAt.substring(0, 10)} ${result.createdAt.substring(11, 19)}`,
							commentCount: result.commentCount as number,
							comments: result.comments as Comments[],
							likeCount: result.likeCount as number,
							country: null,
							state: null,
							city: null,
							tags: result.tags as string,
							profilepicURL: result.user.profilepicURL,
							isLiked: result.isLiked,
							webLink: webLink || null,
							youtubeLink: youtubeLink || null,
							internalURL: result.media && result.media[0] ? result.media[0].internalURL : null,
							isAnonymous: true,
							isOwner: result.isOwner === true,
							shareHash: result.shareHash,
						}
					: {
							id: postId ? parseInt(postId, 10) : 0,
							title: result.title as string,
							content: result.body as string,
							format: mediaFormat.substring(0, 5),
							image_url: file,
							name: result.user.name as string,
							location:
								result.media && result.media[0] ? `(${result.media[0].latitude}, ${result.media[0].longitude})` : '',
							time: `${result.createdAt.substring(0, 10)} ${result.createdAt.substring(11, 19)}`,
							commentCount: result.commentCount as number,
							comments: result.comments as Comments[],
							likeCount: result.likeCount as number,
							country: null,
							state: null,
							city: null,
							tags: result.tags as string,
							profilepicURL: result.user.profilepicURL,
							isLiked: result.isLiked,
							webLink: webLink || null,
							youtubeLink: youtubeLink || null,
							internalURL: result.media && result.media[0] ? result.media[0].internalURL : null,
							isAnonymous: false,
							isOwner: result.isOwner === true,
							shareHash: result.shareHash,
						};

				set_AllPostData(postData);
				setIsLiked(Boolean(postData?.isLiked) && postData?.isLiked !== 0);

				if (postId) {
					updatePostMetadata(parseInt(postId, 10), {
						likeCount: postData.likeCount,
						isLiked: Boolean(postData.isLiked) && postData.isLiked !== 0,
						commentCount: postData.commentCount,
					});
				}

				if (result.media && result.media[0] && result.media[0].latitude && result.media[0].longitude) {
					reverseGeocode(result.media[0].latitude, result.media[0].longitude).then((geoResult) => {
						set_AllPostData((prevData) => {
							if (prevData) {
								return { ...prevData, ...geoResult };
							}
							return prevData;
						});
					});
				}
			} catch (err) {
				console.log(err);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, [postId, updatePostMetadata, handlePostGone, shareMode, shareId, shareHash]);
	const patchPostInSessionStorage = (id: number, patch: Partial<AllPostDataType>) => {
		// Update MyFeed
		const newsSaved = sessionStorage.getItem('newsFeedState');
		if (newsSaved) {
			try {
				const parsed = JSON.parse(newsSaved);
				if (parsed.AllPostData) {
					let updated = false;
					const newData = parsed.AllPostData.map((p: AllPostDataType) => {
						if (Number(p.id) === id) {
							updated = true;
							return { ...p, ...patch };
						}
						return p;
					});
					if (updated) {
						sessionStorage.setItem('newsFeedState', JSON.stringify({ ...parsed, AllPostData: newData }));
					}
				}
			} catch (e) {}
		}

		// Update ExploreFeed
		const exploreSaved = sessionStorage.getItem('exploreFeedState');
		if (exploreSaved) {
			try {
				const parsed = JSON.parse(exploreSaved);
				let updated = false;
				const patchArray = (arr: any[]) => {
					if (!arr) return arr;
					return arr.map((p: AllPostDataType) => {
						if (Number(p.id) === id) {
							updated = true;
							return { ...p, ...patch };
						}
						return p;
					});
				};
				const newPosts = patchArray(parsed.posts);
				const newOriginalPosts = patchArray(parsed.originalPosts);
				if (updated) {
					sessionStorage.setItem('exploreFeedState', JSON.stringify({
						...parsed,
						posts: newPosts,
						originalPosts: newOriginalPosts
					}));
				}
			} catch (e) {}
		}
	};

	useEffect(() => {
		if (AllPostData && AllPostData.id) {
			patchPostInSessionStorage(AllPostData.id, AllPostData);
		}
	}, [AllPostData]);


	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target as Node)) {
				setShareDropdownOpen(false);
			}
			if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
				setSortDropdownOpen(false);
			}
			if (commentDropdownRef.current && !commentDropdownRef.current.contains(event.target as Node)) {
				// If we click on the dots button itself, let the button's own onClick handle the toggle
				if (!(event.target as HTMLElement).closest('.sph-comment-dots')) {
					setOpenCommentDotsId(null);
				}
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const createComment = async (body: string) => {
		// Read-only share mode: gate behind login instead of posting.
		if (shareMode) {
			setShowLoginPrompt(true);
			return;
		}
		const requestBody = {
			postId: postId ? parseInt(postId, 10) : null,
			parentId: CommentParentId !== null ? CommentParentId : null,
			reportId: null,
			body: body,
			isAnonymous: false,
		};

		const requestOptions = {
			method: 'POST',
			body: JSON.stringify(requestBody),
			redirect: 'follow' as RequestRedirect,
			credentials: 'include' as RequestCredentials,
			headers: {
				'Content-Type': 'application/json',
			},
		};

		try {
			const response = await fetch(`${BACKEND_URL}/api/createComment`, requestOptions);
			// The post may have been deleted (here or from another device) while
			// this page was open. Bounce back gracefully instead of erroring.
			if (response.status === 404) {
				handlePostGone();
				return;
			}
			const result = await response.text();
			const tempId = parseInt(result, 10);
			if (isNaN(tempId)) throw new Error('Invalid comment ID from server');

			// Update optimistic state
			const cmt: Comments = {
				commentId: tempId, // actual id from backend
				parentId: CommentParentId,
				isAnonymous: false,
				user: {
					name: user?.name!,
					id: user?.id!,
					profilepicURL: user?.profilepicURL!,
				},
				likeCount: 0,
				createdAt: new Date().toISOString(),
				body: body,
			};

			if (AllPostData) {
				const temp = [...AllPostData.comments, cmt];
				const newCount = AllPostData.commentCount + 1;
				set_AllPostData({ ...AllPostData, comments: temp, commentCount: newCount });
				if (postId) updatePostMetadata(parseInt(postId, 10), { commentCount: newCount });
			}

			set_CommentBody('');
			set_CommentParentId(null);
			set_CommentName('');
		} catch (err) {
			console.log('Error creating comment', err);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			if (CommentBody.trim() !== '') createComment(CommentBody);
		}
	};

	const reactPost = async (type: string) => {
		const requestBody = {
			postId: postId ? parseInt(postId, 10) : null,
			type: type,
			isAnonymous: user?.name === 'Anonymous',
		};

		const requestOptions = {
			method: 'POST',
			body: JSON.stringify(requestBody),
			redirect: 'follow' as RequestRedirect,
			credentials: 'include' as RequestCredentials,
			headers: {
				'Content-Type': 'application/json',
			},
		};
		try {
			const res = await fetch(`${BACKEND_URL}/api/react`, requestOptions);
			// Post deleted elsewhere — undo the optimistic like and bounce back.
			if (res.status === 404) {
				handlePostGone();
				return false;
			}
			return true;
		} catch (err) {
			console.log(err);
			return true;
		}
	};

	const handleLikeClick = () => {
		if (shareMode) {
			setShowLoginPrompt(true);
			return;
		}
		if (AllPostData && postId) {
			const newIsLiked = !isLiked;
			const newLikeCount = newIsLiked ? AllPostData.likeCount + 1 : Math.max(0, AllPostData.likeCount - 1);
			const type = newIsLiked ? 'like' : 'dislike';
			set_AllPostData({ ...AllPostData, likeCount: newLikeCount, isLiked: newIsLiked });
			setIsLiked(newIsLiked);
			reactPost(type);
			updatePostMetadata(parseInt(postId, 10), { likeCount: newLikeCount, isLiked: newIsLiked });
		}
	};

	const handleDeleteComment = async (commentId: number) => {
		try {
			const requestOptions = {
				method: 'DELETE',
				redirect: 'follow' as RequestRedirect,
				credentials: 'include' as RequestCredentials,
				headers: {
					'Content-Type': 'application/json',
				},
			};
			const res = await fetch(`${BACKEND_URL}/api/deleteComment/${commentId}`, requestOptions);

			// Even if 404 (already deleted), we want to remove it from UI
			if (res.ok || res.status === 404) {
				if (AllPostData) {
					// Function to recursively find direct child replies
					const findReplies = (cId: number, allComments: Comments[]): number[] => {
						const directReplies = allComments.filter((c) => c.parentId === cId).map((c) => c.commentId);
						return [...directReplies, ...directReplies.flatMap((id) => findReplies(id, allComments))];
					};

					const idsToRemove = [commentId, ...findReplies(commentId, AllPostData.comments)];
					const filteredComments = AllPostData.comments.filter((c) => !idsToRemove.includes(c.commentId));
					const newCommentCount = Math.max(0, AllPostData.commentCount - idsToRemove.length);

					set_AllPostData({
						...AllPostData,
						comments: filteredComments,
						commentCount: newCommentCount,
					});

					if (postId) {
						updatePostMetadata(parseInt(postId, 10), { commentCount: newCommentCount });
					}
				}
			} else {
				console.error('Failed to delete comment:', await res.text());
			}
		} catch (error) {
			console.error('Error deleting comment:', error);
		}
	};

	const CommentTree = (oldest: boolean = true, comments: Comments[]): JSX.Element => {
		const mp = new Map<number, Comments[]>();
		comments.forEach((val) => {
			if (val.parentId === null) mp.set(val.commentId, [val]);
			else mp.get(val.parentId)?.push(val);
		});

		const keys: number[] = [];
		const sortedMap = new Map<number, Comments[]>();
		mp.forEach((value, key) => keys.push(key));

		if (oldest) {
			keys.sort((a, b) => a - b).forEach((val) => sortedMap.set(val, mp.get(val)!));
		} else {
			keys.sort((a, b) => b - a).forEach((val) => sortedMap.set(val, mp.get(val)!));
		}

		const temp: JSX.Element[] = [];
		sortedMap.forEach((value) => {
			value.forEach((comment) => {
				temp.push(
					<div
						className={`sph-comment ${comment.parentId != null ? 'sph-comment--reply' : ''}`}
						key={comment.commentId}
					>
						<div className='sph-comment-avatar'>
							<ProfileImage
								name={comment.user?.name}
								pfpURL={comment.user?.profilepicURL}
								className='sph-comment-pfp'
								isAnonymous={comment.isAnonymous}
							/>
						</div>
						<div className='sph-comment-bubble'>
							<div className='sph-comment-header'>
								<span className='sph-comment-name'>{comment.user.name}</span>
								<div className='relative'>
									<button
										className='sph-comment-dots'
										title='More options'
										onClick={() =>
											shareMode
												? setShowLoginPrompt(true)
												: setOpenCommentDotsId(openCommentDotsId === comment.commentId ? null : comment.commentId)
										}
									>
										<svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
											<circle cx='5' cy='12' r='2' />
											<circle cx='12' cy='12' r='2' />
											<circle cx='19' cy='12' r='2' />
										</svg>
									</button>
									{openCommentDotsId === comment.commentId && (
										<div className='sph-comment-dropdown' ref={commentDropdownRef}>
											{comment.user.id === user?.id ? (
												<button
													className='sph-comment-dropdown-item sph-comment-dropdown-item--danger'
													onClick={() => {
														setOpenCommentDotsId(null);
														handleDeleteComment(comment.commentId);
													}}
												>
													Delete
												</button>
											) : (
												<button
													className='sph-comment-dropdown-item'
													onClick={() => {
														setOpenCommentDotsId(null);
														// TODO: Implement Report Action
														console.log('Report comment', comment.commentId);
													}}
												>
													Report
												</button>
											)}
										</div>
									)}
								</div>
							</div>
							<div className='sph-comment-body'>{comment.body}</div>
							<div className='sph-comment-footer'>
								<button
									className='sph-comment-reply-btn'
									onClick={() => {
										if (shareMode) { setShowLoginPrompt(true); return; }
											set_CommentParentId(comment.parentId == null ? comment.commentId : comment.parentId);
										set_CommentName(comment.user.name);
										setTimeout(() => commentInputRef.current?.focus(), 0);
									}}
								>
									Reply
								</button>
								<span className='sph-comment-time'>{formatCustomDistanceToNow(moment.utc(comment.createdAt).toDate())}</span>
							</div>
						</div>
					</div>
				);
			});
		});

		return <>{temp.map((val) => val)}</>;
	};

	// Post was deleted (here or from another device). Show a brief notice while
	// we bounce back to a freshly-loaded feed, instead of a dead-end screen.
	if (postGone) {
		return (
			<div className='sph-gone-screen'>
				<div className='sph-gone-card'>
					<div className='sph-gone-icon'>
						<Trash2 size={26} />
					</div>
					<h3 className='sph-gone-title'>This post no longer exists</h3>
					<p className='sph-gone-text'>It may have been deleted. Returning you to the feed…</p>
				</div>
			</div>
		);
	}

	if (isLoading) {
		return <LoadingSpinner />;
	}

	if (!AllPostData) {
		return (
			<div className='text-center p-8 text-gray-500 bg-gray-100 min-h-screen'>
				Post not found or could not be loaded.
			</div>
		);
	}

	return (
		<div className='sph-page-bg'>
			<CustomModal show={showModal} handleClose={handleCloseModal} imageUrl={AllPostData.image_url} />

			{/* Transient toast (post deleted / post gone / errors) */}
			{toastMessage && (
				<div className='sph-toast' role='status'>
					{toastMessage}
				</div>
			)}

			{/* Login prompt — shown when a logged-out share-mode viewer tries to
			    like, comment or reply. Failsafe even if a control isn't grayed out. */}
			{showLoginPrompt && (
				<div className='sph-confirm-overlay' onClick={() => setShowLoginPrompt(false)}>
					<div className='sph-confirm-card' onClick={(e) => e.stopPropagation()}>
						<h3 className='sph-confirm-title'>Log in to interact</h3>
						<p className='sph-confirm-text'>
							Log in to like, comment and join the conversation on B-Reporter.
						</p>
						<div className='sph-confirm-actions'>
							<button
								className='sph-confirm-cancel'
								onClick={() => setShowLoginPrompt(false)}
							>
								Not now
							</button>
							<button
								className='sph-confirm-login'
								onClick={() => navigate('/login', { state: { from: location.pathname } })}
							>
								Log in
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Admin reveal-author popup. One-time view: closing wipes the identity
			    from state, so a second reveal has to re-fetch from the backend. */}
			{revealedAuthor && (
				<div className='sph-confirm-overlay' onClick={closeRevealPopup}>
					<div className='sph-confirm-card' onClick={(e) => e.stopPropagation()}>
						<h3 className='sph-confirm-title'>Post author</h3>
						<p className='sph-confirm-text'>
							Identity of this anonymous post's author. Visible to admins only — it is not kept after you close this.
						</p>
						<div className='flex flex-col gap-2 mb-4 text-left'>
							<div className='flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5'>
								<div className='min-w-0'>
									<p className='text-[9px] font-bold text-gray-400 uppercase leading-none mb-1 m-0'>Name</p>
									<p className='text-sm font-semibold text-gray-800 m-0 truncate'>{revealedAuthor.name}</p>
								</div>
								<button
									className='p-1.5 hover:bg-gray-200 rounded-lg transition-colors text-gray-400 hover:text-indigo-600 shrink-0'
									title='Copy name'
									onClick={() => handleRevealCopy('name', revealedAuthor.name)}
								>
									{copiedField === 'name' ? <Check size={14} className='text-green-600' /> : <Copy size={14} />}
								</button>
							</div>
							<div className='flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5'>
								<div className='min-w-0'>
									<p className='text-[9px] font-bold text-gray-400 uppercase leading-none mb-1 m-0'>Phone number</p>
									<p className='text-sm font-semibold text-gray-800 m-0 truncate'>+{revealedAuthor.phoneNumber}</p>
								</div>
								<button
									className='p-1.5 hover:bg-gray-200 rounded-lg transition-colors text-gray-400 hover:text-indigo-600 shrink-0'
									title='Copy phone number'
									onClick={() => handleRevealCopy('phone', revealedAuthor.phoneNumber)}
								>
									{copiedField === 'phone' ? <Check size={14} className='text-green-600' /> : <Copy size={14} />}
								</button>
							</div>
						</div>
						<div className='sph-confirm-actions'>
							<button className='sph-confirm-cancel' onClick={closeRevealPopup}>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Delete confirmation */}
			{showDeleteConfirm && (
				<div className='sph-confirm-overlay' onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
					<div className='sph-confirm-card' onClick={(e) => e.stopPropagation()}>
						<div className='sph-confirm-icon'>
							<Trash2 size={22} />
						</div>
						<h3 className='sph-confirm-title'>Delete post?</h3>
						<p className='sph-confirm-text'>
							Are you sure you want to delete this post? This action cannot be undone.
						</p>
						<div className='sph-confirm-actions'>
							<button
								className='sph-confirm-cancel'
								onClick={() => setShowDeleteConfirm(false)}
								disabled={isDeleting}
							>
								Cancel
							</button>
							<button className='sph-confirm-delete' onClick={handleDeletePost} disabled={isDeleting}>
								{isDeleting ? 'Deleting…' : 'Delete'}
							</button>
						</div>
					</div>
				</div>
			)}

			<div className='sph-outer'>
				<div className='sph-card'>
					{/* ── HEADER ─────────────────────────────────── */}
					<div className='sph-header'>
						<div className='sph-header-left'>
							<button className='sph-back-btn' onClick={returnToFeed} title='Go back'>
								<svg
									width='20'
									height='20'
									viewBox='0 0 24 24'
									fill='none'
									stroke='currentColor'
									strokeWidth='2.2'
									strokeLinecap='round'
									strokeLinejoin='round'
								>
									<polyline points='15 18 9 12 15 6' />
								</svg>
							</button>
							<div className='sph-avatar'>
								<ProfileImage
									name={AllPostData.name}
									pfpURL={AllPostData.profilepicURL}
									isAnonymous={AllPostData.isAnonymous}
								/>
							</div>
							<span className='sph-sender-name'>{AllPostData.name}</span>
							{isAdmin && !shareMode && isDesktop && AllPostData.isAnonymous && (
								<button
									className='flex items-center gap-1.5 px-3 py-1 ml-2 rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors text-xs font-semibold border border-indigo-100 shrink-0 disabled:opacity-60'
									title='Reveal author (admin only)'
									disabled={isRevealing}
									onClick={handleRevealAuthor}
								>
									{isRevealing ? <Loader2 size={14} className='animate-spin' /> : <Eye size={14} />}
									<span>Reveal</span>
								</button>
							)}
						</div>

						<div className='sph-header-right'>
							<div className='sph-meta'>
								<div className='sph-meta-row'>
									<span className='sph-meta-label'>Created on:</span>
									<span className='sph-meta-value sph-meta-value-line1'>
										{moment.utc(AllPostData.time).format('DD MMM YYYY, HH:mm')}
										<span className='sph-hide-mobile'> ({formatCustomDistanceToNow(moment.utc(AllPostData.time).toDate())})</span>
									</span>
								</div>
								{/* YouTube/web link posts are created from the admin panel,
								    which has no location picker and falls back to a default
								    location. That coordinate is meaningless, so hide the
								    Location row (label, address line, and map pin) for them. */}
								{!AllPostData.youtubeLink && !AllPostData.webLink && (
									<div className='sph-meta-row'>
										<span className='sph-meta-label'>Location:</span>
										<div className='sph-meta-value sph-meta-value-line2'>
											<SophisticatedAddress
												parts={AllPostData.address_parts || []}
												locationQuery={AllPostData.location}
											/>
										</div>
									</div>
								)}
							</div>
							{AllPostData.isOwner && (
								<>
									<div className='sph-vbar' />
									<button
										className='sph-dots-btn sph-delete-btn'
										title='Delete post'
										onClick={() => setShowDeleteConfirm(true)}
									>
										<Trash2 size={18} />
									</button>
								</>
							)}
						</div>
					</div>

					{/* ── IMAGE / MEDIA ───────────────────────────── */}
					<div className='sph-image-wrap'>
						{AllPostData.format === 'image' && (
							<PostImage
								className='sph-image'
								alt={AllPostData.title}
								src={AllPostData.image_url}
								onClick={handleOpenModal}
							/>
						)}
						{AllPostData.format !== 'image' && AllPostData.webLink && (
							<div className='sph-video-wrap'>
								<iframe
									src={AllPostData.webLink}
									width='100%'
									height='400px'
									title='External Website'
									className='w-full border-none'
									sandbox='allow-scripts allow-same-origin allow-popups allow-forms allow-presentation'
									referrerPolicy='no-referrer'
									allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure'
								/>
							</div>
						)}
						{AllPostData.format !== 'image' && AllPostData.youtubeLink && (
							<div className='sph-video-wrap'>
								<iframe
									src={AllPostData.youtubeLink}
									width='100%'
									height='420px'
									frameBorder='0'
									title='YouTube Video'
									className='w-full border-none'
									allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure'
								/>
							</div>
						)}
					</div>

					{/* ── TITLE + DESCRIPTION + HASHTAGS ─────────── */}
					<div className='sph-body'>
						<div className='flex justify-between items-start gap-3'>
							{AllPostData.title && <h2 className='sph-title'>{AllPostData.title}</h2>}
							{!AllPostData.youtubeLink && (
								<button
									className='flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors text-sm font-semibold border border-amber-100 shrink-0 mb-2'
									title='Risko-Meter'
									onClick={(e) => {
										e.stopPropagation();
										if (AllPostData.internalURL) {
											setRiskoMeterMediaId(AllPostData.internalURL);
										}
									}}
								>
									<Gauge size={16} />
									<span>Risk</span>
								</button>
							)}
						</div>

						<p className={`sph-description ${!AllPostData.content ? 'sph-description--empty' : ''}`}>
							{AllPostData.content || 'No description'}
						</p>

						{AllPostData.tags && (
							<div className='flex flex-wrap gap-1 mt-2 mb-2'>
								{AllPostData.tags.split(',').map(
									(tag, index) =>
										tag.trim() !== '' && (
											<span key={index} className='text-blue-600 text-opacity-80 text-sm font-medium leading-none mr-2'>
												#{tag.trim()}
											</span>
										)
								)}
							</div>
						)}
					</div>

					<div className='sph-divider' />

					{/* ── CUSTOM ACTION BAR (SINGLE LINE) ───────── */}
					<div className='flex flex-nowrap items-center px-4 py-3 gap-3'>
						{/* Compact Comment Input */}
						<div className={`flex-1 flex gap-2 min-w-0 bg-gray-50 rounded-full p-1 border border-gray-200 focus-within:border-blue-500 focus-within:ring-0 transition-colors items-center h-10 ${shareMode ? 'opacity-70' : ''}`}>
							{CommentName && (
								<div className='bg-blue-900 text-white text-[10px] h-8 pl-3 pr-1 rounded-full flex items-center shrink-0 font-bold'>
									<span className='mb-[0.5px]'>@{CommentName}</span>
									<button
										onClick={() => {
											set_CommentName('');
											set_CommentParentId(null);
										}}
										className='ml-1 w-6 h-6 text-white hover:bg-blue-800 rounded-full flex items-center justify-center transition-colors'
										title='Remove reply'
									>
										<X size={14} strokeWidth={3} />
									</button>
								</div>
							)}
							<input
								ref={commentInputRef}
								type='text'
								className='flex-1 bg-transparent border-none text-sm outline-none focus:outline-none focus:ring-0 px-2 py-0 h-full min-w-0 shadow-none'
								placeholder={shareMode ? 'Log in to comment…' : 'Write a comment…'}
								value={CommentBody}
								readOnly={shareMode}
								onClick={() => { if (shareMode) setShowLoginPrompt(true); }}
								onChange={(e) => set_CommentBody(e.target.value)}
								onKeyDown={handleKeyPress}
							/>
							<button
								className='shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-all disabled:opacity-50 disabled:hover:bg-blue-600 active:scale-95'
								disabled={shareMode ? false : !CommentBody.trim()}
								onClick={() => {
									if (shareMode) { setShowLoginPrompt(true); return; }
									if (CommentBody.trim() !== '') createComment(CommentBody);
								}}
							>
								<svg
									width='14'
									height='14'
									viewBox='0 0 24 24'
									fill='none'
									stroke='currentColor'
									strokeWidth='2.5'
									strokeLinecap='round'
									strokeLinejoin='round'
								>
									<line x1='22' y1='2' x2='11' y2='13'></line>
									<polygon points='22 2 15 22 11 13 2 9 22 2'></polygon>
								</svg>
							</button>
						</div>

						{/* Actions Container */}
						<div className='flex items-center shrink-0 ml-4 py-1 gap-4'>
							{/* Like Button */}
							<button
								onClick={handleLikeClick}
								className={`flex items-center gap-2 transition-colors hover:scale-105 ${shareMode ? 'opacity-60' : ''} ${isLiked ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
							>
								<span className='font-semibold text-sm whitespace-nowrap tabular-nums min-w-[1.2rem] text-right'>{makeNumber(AllPostData.likeCount)}</span>
								<Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
							</button>

							{/* Share Button Placeholder */}
							<div className='relative flex items-center justify-center' ref={shareDropdownRef}>
								<button
									onClick={() => setShareDropdownOpen(!isShareDropdownOpen)}
									className='hover:scale-105 text-gray-500 hover:text-gray-700 transition'
								>
									<Share2 className='w-5 h-5' />
								</button>
								{isShareDropdownOpen && (
									<div className='absolute right-0 bottom-full mb-2 z-50' onClick={(e) => e.stopPropagation()}>
										<SharingTab url={`${WEB_BASE_URL}/share/${postId}/${AllPostData.shareHash}`} title={AllPostData.title} />
									</div>
								)}
							</div>
						</div>
					</div>

					<div className='sph-divider' />

					{/* ── COMMENTS HEADER BAR ───────────────────── */}
					<div className='sph-comments-bar bg-white pt-2'>
						<span className='sph-comment-count font-semibold text-sm'>Comments ({AllPostData.commentCount})</span>
						<div className='sph-sort-row'>
							<span className='sph-sort-label font-medium pr-2'>Sort by</span>
							<div className='relative' ref={sortDropdownRef}>
								<button
									onClick={() => setSortDropdownOpen(!isSortDropdownOpen)}
									className='sph-sort-btn flex items-center justify-between gap-1 w-[100px]'
									type='button'
								>
									<span>{Oldest ? 'Oldest' : 'Newest'}</span>
									<svg
										width='12'
										height='12'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='2'
										strokeLinecap='round'
										strokeLinejoin='round'
										className={`shrink-0 transition-transform duration-200 ${isSortDropdownOpen ? 'rotate-180' : ''}`}
									>
										<polyline points='6 9 12 15 18 9'></polyline>
									</svg>
								</button>
								{isSortDropdownOpen && (
									<ul className='absolute left-0 top-full mt-1 w-full bg-white border border-gray-100 rounded-md shadow-lg z-20 overflow-hidden list-none p-0 m-0'>
										<li className='w-full'>
											<button
												className='sph-sort-option py-1.5 text-sm text-gray-700 hover:bg-gray-50 font-medium flex justify-center items-center w-full transition-colors'
												onClick={() => {
													set_Oldest(false);
													setSortDropdownOpen(false);
												}}
											>
												Newest
											</button>
										</li>
										<li className='w-full border-t border-gray-50'>
											<button
												className='sph-sort-option py-1.5 text-sm text-gray-700 hover:bg-gray-50 font-medium flex justify-center items-center w-full transition-colors'
												onClick={() => {
													set_Oldest(true);
													setSortDropdownOpen(false);
												}}
											>
												Oldest
											</button>
										</li>
									</ul>
								)}
							</div>
						</div>
					</div>

					{/* ── COMMENTS LIST ───────────────────────────── */}
					<div className='sph-comments-list pb-8'>
						{CommentTree(Oldest, AllPostData.comments)}
						{AllPostData.comments.length === 0 && (
							<div className='text-center py-8 px-4 text-gray-400 bg-gray-50 rounded-lg mt-2 border border-dashed border-gray-200'>
								<p className='font-medium text-gray-500 mb-1'>No comments yet</p>
								<p className='text-sm'>Be the first to share your thoughts!</p>
							</div>
						)}
					</div>
				</div>
				{/* /.sph-card */}
			</div>
			{/* /.sph-outer */}

			{riskoMeterMediaId && <RiskoMeter mediaId={riskoMeterMediaId} onClose={() => setRiskoMeterMediaId(null)} />}
		</div>
	);
}

export default SinglePostHelper;
